import React from "react";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { EditTextFieldDialog } from "../core/EditTextFieldDialog";
import { Buttons } from "../core/EditableListItemUrl";
import { useBggGameStats, useUpdateGameV2 } from "@seabrookstudios/pitch2table";
import { formatNumber } from "../games/royalties/currency-formatting";
import { getBrandIcon } from "../core/linked-service-icons";
import { enqueueSnackbar } from "notistack";
import { Features } from "./features";

/**
 * @param {object} props
 * @param {import('@seabrookstudios/types').GameId} props.gameId
 * @param {import('@seabrookstudios/types').Url|undefined} props.bgg
 */
export const BggRankField = ({ gameId, bgg }) => {
  const { data: bggStats } = useBggGameStats(gameId, bgg);

  const onUpdateSuccess = () => {
    enqueueSnackbar("BGG link updated successfully", { variant: "success" });
  };

  const onUpdateError = () => {
    enqueueSnackbar("Failed to update BGG link", { variant: "error" });
  };

  const updateGame = useUpdateGameV2(gameId, onUpdateSuccess, onUpdateError);

  /**
   * @param {import('@seabrookstudios/types').Url} newBgg
   */
  const updateBggLink = async (newBgg) => {
    await updateGame.mutateAsync({
      bgg: newBgg,
      sizzle: undefined,
      personalGoal: undefined,
      customAuthors: undefined,
      gameType: undefined,
      referenceId: undefined,
      tags: undefined,
      feedbackEnabled: undefined,
    });
  };

  const hasUrl = bgg && bgg.length > 0;
  const bggIcon = getBrandIcon("https://boardgamegeek.com");

  // Display the rank if available, otherwise show "N/A" or prompt to set URL
  const getDisplayValue = () => {
    if (!hasUrl) {
      return "Tap to set BGG URL";
    }

    if (bggStats?.ranking) {
      return `Rank: ${formatNumber(bggStats.ranking)}`;
    }

    return "N/A";
  };

  const BggIcon = () => {
    if (!bggIcon) {
      return null;
    }

    return (
      <img
        alt="BoardGameGeek"
        height="20"
        src={bggIcon.fullPath ? bggIcon.slug : `https://cdn.simpleicons.org/${bggIcon.slug}?viewbox=auto`}
      />
    );
  };

  if (!Features.BggRank) {
    return null;
  }

  return (
    <Stack direction="column">
      <ListItem>
        <ListItemIcon>
          <BggIcon />
        </ListItemIcon>
        <ListItemText>
          <EditTextFieldDialog
            onSave={updateBggLink}
            title="BGG Rank"
            initialValue={bgg}
            isBusy={updateGame.isPending}
            helperText="Link to game on BoardGameGeek."
            multiline={false}
            onEnterDisabled={false}
          >
            <Tooltip title="Click to edit BGG link">
              <ListItemText
                primary="BGG Rank"
                secondary={getDisplayValue()}
                sx={{ maxWidth: "66ch", whiteSpace: "pre-wrap" }}
              />
            </Tooltip>
          </EditTextFieldDialog>
        </ListItemText>
        {hasUrl && <Buttons value={bgg} />}
      </ListItem>
    </Stack>
  );
};
